import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { DataTable, type Column } from '../../components/data-table';
import { SkeletonRows } from '../../components/skeleton';
import { PageHeader } from '../../components/page-header';
import { extractErr } from '../../api/error-message';
import { useDebouncedValue } from '../../components/table-search';

// Wired to GET /admin/audit-logs — the append-only trail of admin/system
// actions. Paged and searched by the API (page/limit/q), so search reaches the
// whole history, not just the most recent entries; the query matches every
// column the table shows (ref, actor, action, entity, IP, date).

const PER_PAGE = 15;

interface AuditRow {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  ip: string | null;
  createdAt: string;
  adminUser: { id: string; name: string; email: string } | null;
  /** ADMIN | SELLER | CUSTOMER | DELIVERY_PARTNER | SYSTEM — null on older rows. */
  actorType?: string | null;
  actorId?: string | null;
}

const ACTOR_LABEL: Record<string, string> = {
  ADMIN: 'Admin',
  SELLER: 'Seller',
  CUSTOMER: 'Customer',
  DELIVERY_PARTNER: 'Delivery',
  SYSTEM: 'System',
};

/**
 * Who did it. Rows written before actor tracking only carry `adminUser`; an
 * unattributed older row is shown as "Unknown", not "System", since it may have
 * been a seller or customer action.
 */
function actorOf(r: AuditRow): { type: string; name: string } {
  if (r.adminUser) return { type: 'Admin', name: r.adminUser.name };
  if (r.actorType) {
    const type = ACTOR_LABEL[r.actorType] ?? r.actorType;
    return { type, name: r.actorType === 'SYSTEM' ? 'System' : `${type} #${r.actorId ?? '?'}` };
  }
  return { type: '—', name: 'Unknown' };
}

// Heuristic risk tint from the action verb (no risk column in the schema).
function risk(action: string): string {
  const a = action.toLowerCase();
  if (a.includes('delete') || a.includes('force') || a.includes('reject') || a.includes('suspend')) return 'badge-danger';
  if (a.includes('config') || a.includes('approve') || a.includes('payout') || a.includes('override') || a.includes('assign')) return 'badge-warning';
  return '';
}

export function AuditTrailPage() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // Skeleton on the first fetch only; later fetches keep the table (and the
  // search box's focus) in place.
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const q = useDebouncedValue(search.trim(), 300);

  // A new query is a new result set — back to page 1.
  useEffect(() => { setPage(1); }, [q]);

  useEffect(() => {
    let alive = true;
    api
      .get<{ items: AuditRow[]; total: number }>('/admin/audit-logs', {
        params: { page, limit: PER_PAGE, ...(q ? { q } : {}) },
      })
      .then(({ data }) => { if (alive) { setRows(data.items); setTotal(data.total); } })
      .catch((err) => { if (alive) toast.error(extractErr(err)); })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [page, q]);

  const columns: Column<AuditRow>[] = [
    { key: 'id', label: 'Ref', render: (r) => <span className="sku text-ink-500">#{r.id}</span> },
    {
      key: 'actorType',
      label: 'Actor',
      render: (r) => {
        const a = actorOf(r);
        return (
          <div>
            <p className="font-medium text-ink-900">{a.name}</p>
            <p className="text-[12.5px] text-ink-500">{a.type}</p>
          </div>
        );
      },
    },
    { key: 'action', label: 'Action', render: (r) => <span className={`badge ${risk(r.action)}`}>{r.action}</span> },
    { key: 'entityType', label: 'Entity', render: (r) => <span className="text-ink-700">{r.entityType}{r.entityId ? ` #${r.entityId}` : ''}</span> },
    { key: 'ip', label: 'IP', render: (r) => <span className="sku text-ink-500">{r.ip ?? '—'}</span> },
    { key: 'createdAt', label: 'Time', render: (r) => new Date(r.createdAt).toLocaleString('en-IN') },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit trail"
        description="Append-only log of admin, seller, customer &amp; system actions"
      />

      {!loaded ? (
        <div className="panel overflow-hidden"><SkeletonRows cols={6} /></div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          rowKey={(r) => r.id}
          perPage={PER_PAGE}
          serverPagination={{ page, totalItems: total, onPageChange: setPage }}
          searchPlaceholder="Search by actor, action, entity, IP, ref or date"
          searchValue={search}
          onSearchChange={setSearch}
          exportName="audit-log"
        />
      )}
    </div>
  );
}
